import * as Sentry from '@sentry/vue';
import { makeBrowserOfflineTransport, makeFetchTransport } from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { isDemographicKey } from '@/shared/lib/demographic-keys';
import { setNetworkContext } from './context';
import { state } from './state';

// Sentry DSN — The Artisan Kiosk Frontend project
const SENTRY_DSN =
  'https://8a27bbd3ac8a73e3fedbf18efea86522@o4506991194996736.ingest.us.sentry.io/4510716218966016';

// Backend API endpoints for distributed tracing propagation.
const TRACE_PROPAGATION_TARGETS = [
  'localhost',
  /^https:\/\/.*\.theartisan\.ai\/api/,
  /^https:\/\/api\.theartisan\.ai/,
  /^\/api\//,
];

const DEV_HOSTNAME_PATTERNS: (string | RegExp)[] = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  /\.local$/,
  /\.localhost$/,
  /\.ngrok\.io$/,
  /\.ngrok-free\.app$/,
  /\.loca\.lt$/,
  /^192\.168\./,
  /^10\./,
  /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
];

export const BROWSER_PLATFORM_IGNORE_ERRORS: RegExp[] = [
  // Firefox's ReportingObserver surfaces every browser API deprecation as a
  // Sentry event: InstallTrigger, MutationEvent, etc. (KIOSK-FRONTEND-N0).
  /^ReportingObserver \[deprecation\]/,
  // ResizeObserver fires this when an observer callback triggers another
  // resize within the same frame. Benign per-spec browser behavior.
  /^ResizeObserver loop /,
  // Chrome on iOS sometimes throws this through framework boundaries during
  // page tear-down; not actionable from app code.
  /^Non-Error promise rejection captured with value: undefined$/,
];

export function isIgnoredBrowserPlatformErrorMessage(message: string): boolean {
  return BROWSER_PLATFORM_IGNORE_ERRORS.some((pattern) =>
    pattern.test(message)
  );
}

// Synthetic traffic that must not pollute production error stats: the CI
// visual-test harness (partner "visual-test-partner") and the EUR payment
// test kiosk. NOT venue demo kiosks — those are real deployments.
const TEST_TRAFFIC_PARTNERS = new Set(['visual-test-partner']);
const TEST_TRAFFIC_SLUGS = new Set(['test_eur_par']);

export function isTestTraffic(slug?: string, partner?: string): boolean {
  return (
    (partner !== undefined && TEST_TRAFFIC_PARTNERS.has(partner)) ||
    (slug !== undefined && TEST_TRAFFIC_SLUGS.has(slug))
  );
}

// The browser is the trace head and the backend inherits its decision
// (parent_sampled), so this rate also governs backend span ingest for
// kiosk-originated traffic — a multiplier, not an independent knob.
// Errors are unaffected: sampling gates traces, never error delivery.
export const KIOSK_TRACES_SAMPLE_RATE = 0.1;

// Telemetry about telemetry. These two transactions exist only to trace the
// POST that ships the kiosk's own analytics — 140k root transactions a week,
// roughly half this project's transaction volume, describing the act of
// reporting rather than anything a customer did. Errors raised inside them
// still ship at 100%: sampling gates traces, never error delivery.
// `idle_watcher.reset` is deliberately NOT here — it traces a real session
// teardown, which is product behavior worth seeing.
// The kiosk config reads start a transaction of their own only in the 5-minute
// refresh on an idle Home (at the boot and on a step they sit under the page
// load or the navigation): 288 refreshes a day per kiosk of housekeeping.
const SERVICE_TRANSACTIONS = new Set([
  'analytics.submit',
  'analytics.flush',
  'api.get_kiosk',
  'api.get_partner',
  'api.get_style',
]);

interface TraceSamplingContext {
  name?: string;
  parentSampled?: boolean;
}

export function traceSampleRate(context: TraceSamplingContext): number {
  // An incoming decision wins outright. The browser is normally the head of
  // the trace so this rarely fires, but when something upstream has already
  // decided, honoring it is what keeps a distributed trace whole instead of
  // splitting it into a sampled half and an unsampled half.
  if (typeof context.parentSampled === 'boolean') {
    return context.parentSampled ? 1 : 0;
  }

  if (context.name !== undefined && SERVICE_TRANSACTIONS.has(context.name)) {
    return 0;
  }

  return KIOSK_TRACES_SAMPLE_RATE;
}

// Vue-router names the home screen "/:slug" before the locale resolves and
// "/:slug/:localeCode" after — the same StepHome component (router.js maps
// both paths to it), so the same bootstrap errors split across two Sentry
// transaction names. Collapse to one so issue lists stop double-counting.
export function normalizeHomeTransaction(
  transaction: string | undefined
): string | undefined {
  return transaction === '/:slug' ? '/:slug/:localeCode' : transaction;
}

// Development runs (vite dev server, Playwright e2e on localhost) used to
// ship events to the production Sentry project — 12 spurious "new issues"
// and elevated-error-rate alert flaps in one day. A DSN-less init keeps the
// full SDK pipeline alive (breadcrumbs, Spotlight sidecar) but sends
// nothing. VITE_SENTRY_FORCE=true restores sending for deliberate
// debugging of the Sentry integration itself. CI is gated separately via
// `enabled` — it must ship nothing even under force.
export function resolveSentryDsn(
  environment: string,
  force: boolean
): string | undefined {
  if (environment === 'development' && !force) return undefined;
  return SENTRY_DSN;
}

// The customer pages and the legacy /portraits links that redirect to them
// run on the phones of visitors. There, the default personal data of the SDK
// (the IP address, and the headers and cookies of a failed request) belongs
// to a visitor. On a kiosk, it belongs to the venue and helps debugging.
export function sendsDefaultPii(pathname: string): boolean {
  return !/^\/(customer|portraits)($|\/)/.test(pathname);
}

function getEnvironment(): 'ci' | 'development' | 'staging' | 'production' {
  // CI declares itself: a workflow runner and a developer's `npm start` both
  // sit on localhost, so only the build/serve environment can tell them apart.
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT === 'ci') return 'ci';
  if (typeof window === 'undefined') return 'production';

  const hostname = window.location?.hostname ?? '';

  for (const pattern of DEV_HOSTNAME_PATTERNS) {
    if (typeof pattern === 'string') {
      if (hostname === pattern) return 'development';
    } else if (pattern.test(hostname)) {
      return 'development';
    }
  }

  if (
    hostname.includes('staging') ||
    hostname.includes('stage') ||
    hostname.includes('dev.')
  ) {
    return 'staging';
  }

  return 'production';
}

// THE-300/301: demographic values must never reach Sentry. The scrub runs in
// every hook below, so a future call site can't reintroduce the leak.

// Depth cap: Sentry's normalizeDepth (default 3) truncates deeper data
// before ingestion, so nothing scrubbable survives past this bound. If
// normalizeDepth is ever raised above 6, raise this with it.
const SCRUB_MAX_DEPTH = 6;

export function scrubDemographicRecord(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>()
): void {
  if (!value || typeof value !== 'object') return;
  // Cycle + depth guards: a Vue reactive/proxy graph attached to extra or
  // contexts can be cyclic or arbitrarily deep — unbounded recursion here
  // overflows the call stack INSIDE the Sentry pipeline (the
  // KIOSK-FRONTEND-3N RangeError shape) and takes the event with it.
  if (seen.has(value)) return;
  seen.add(value);
  if (depth > SCRUB_MAX_DEPTH) return;
  if (Array.isArray(value)) {
    for (const item of value) scrubDemographicRecord(item, depth + 1, seen);
    return;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    try {
      if (isDemographicKey(key)) {
        record[key] = '[FILTERED]';
      } else {
        scrubDemographicRecord(record[key], depth + 1, seen);
      }
    } catch {
      // Frozen or sealed — skip
    }
  }
}

function scrubDemographicSurfaces(event: Sentry.Event): void {
  scrubDemographicRecord(event.tags);
  scrubDemographicRecord(event.extra);
  scrubDemographicRecord(event.contexts);
  scrubDemographicRecord(event.request?.data);
  for (const crumb of event.breadcrumbs ?? []) {
    scrubDemographicRecord(crumb.data);
  }
  for (const span of event.spans ?? []) {
    scrubDemographicRecord((span as { data?: Record<string, unknown> }).data);
  }
}

// httpClientIntegration files each 5xx response as an unhandled error, and the
// SDK then ends the session as crashed, which `kiosk-frontend — crash-free
// sessions` counts. The caller handles the response, so it is no crash. In the
// 14 days to 2026-10-26, 293 of the 315 unhandled production events were such
// responses. The event itself stays, as the only browser-side record of edge
// 5xx (docs/sre-observability.md). The SDK reads `handled` after beforeSend.
function markHttpClientErrorsHandled(event: Sentry.ErrorEvent): void {
  for (const exception of event.exception?.values ?? []) {
    if (exception.mechanism?.type.startsWith('auto.http.client.')) {
      exception.mechanism.handled = true;
    }
  }
}

// CRITICAL: _beforeSend is called by Sentry for EVERY error event.
// An uncaught exception here silently breaks the entire event pipeline and
// drops ALL subsequent events. Every branch is wrapped; on any throw, we
// return the event unmodified rather than lose it.
function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  try {
    const originalError = hint?.originalException as
      | (Error & { context?: { isRequestManuallyAborted?: boolean } })
      | undefined;

    // Drop abort errors — expected during soft restart when
    // restApi.abortAllApiCalls() cancels in-flight requests. AbortError
    // propagates through Vue's error boundary to Sentry's global handler,
    // bypassing RestApi's _captureApiError filter.
    if (originalError?.context?.isRequestManuallyAborted) {
      return null;
    }
    const msg = originalError?.message ?? event?.message ?? '';
    if (msg.includes('signal is aborted')) {
      return null;
    }

    // Drop ui.showError() overlay objects accidentally passed to
    // captureException. They have { type, uuid, hide } instead of Error
    // properties and produce unhelpful "Object captured as exception" events.
    // Guard: 'in' operator throws on non-object primitives — typeof check MUST come first.
    if (
      originalError &&
      typeof originalError === 'object' &&
      'uuid' in originalError &&
      'hide' in originalError
    ) {
      return null;
    }

    // Drop browser-extension autofill noise
    // (KIOSK-FRONTEND-TC/TD/TE seen 2026-05-06). Password managers and
    // form-autofill extensions inject scripts into every page; their
    // internal `xbrowser` / `swbrowser` / `tcbrowser` symbol references
    // throw ReferenceErrors that bubble through window.onerror, get
    // scooped up by Sentry's global handler, and look like real bugs in
    // our customer-download route — but the error is firing inside the
    // extension's <anonymous> code, not ours, with no actionable stack
    // frame in our app. Signature: ReferenceError, message matches
    // `*browser is not defined`, all stack frames are non-app or
    // <anonymous>. Function name is typically `execute_auto_fill` but
    // different extensions pick different names so we don't gate on it.
    if (
      msg &&
      /^[a-zA-Z]{1,5}browser is not defined$/.test(msg) &&
      (event.exception?.values?.[0]?.stacktrace?.frames ?? []).every(
        (f: Sentry.StackFrame) => f.filename === '<anonymous>' || !f.in_app
      )
    ) {
      return null;
    }

    // Synthetic traffic (CI visual tests, payment test kiosk) must not
    // count toward production error stats.
    if (isTestTraffic(state.kioskContext.slug, state.kioskContext.partner)) {
      return null;
    }

    event.transaction = normalizeHomeTransaction(event.transaction);

    if (state.sessionContext.session_id) {
      event.tags = {
        ...event.tags,
        session_id: state.sessionContext.session_id,
      };
    }

    if (originalError?.context) {
      event.contexts = {
        ...event.contexts,
        api_error: originalError.context as Record<string, unknown>,
      };
    }

    // Dev-only opt-out via window.SENTRY_DISABLED. Using cached state.environment
    // avoids re-running the hostname regex on every event.
    if (
      state.environment === 'development' &&
      typeof window !== 'undefined' &&
      (window as Window & { SENTRY_DISABLED?: boolean }).SENTRY_DISABLED
    ) {
      return null;
    }

    scrubDemographicSurfaces(event);
    markHttpClientErrorsHandled(event);

    return event;
  } catch (e) {
    // Pipeline crash guard: partially enriched event is infinitely better
    // than a broken pipeline that silently drops all subsequent events.
    console.warn(
      '[Sentry] _beforeSend threw, passing event through:',
      (e as Error).message
    );
    return event;
  }
}

// Sentry's strict `beforeSendTransaction` type demands TransactionEvent
// which isn't exported. Use the broader Event type here; Sentry only ever
// calls this hook with transaction events at runtime, and we cast at
// registration time below.
function beforeSendTransaction(event: Sentry.Event): Sentry.Event | null {
  try {
    if (isTestTraffic(state.kioskContext.slug, state.kioskContext.partner)) {
      return null;
    }

    event.transaction = normalizeHomeTransaction(event.transaction);

    event.tags = {
      ...event.tags,
      kiosk_slug: state.kioskContext.slug,
      kiosk_partner: state.kioskContext.partner,
    };

    // Strip HTTP body-size data to suppress "Large HTTP Payload" performance
    // issues — all our large payloads (photo upload, image generation,
    // upscale) are expected and not actionable.
    if (event.spans) {
      for (const span of event.spans) {
        // Guard: some Sentry versions freeze span.data; delete on frozen
        // objects throws in strict mode.
        const spanData = (span as { data?: Record<string, unknown> }).data;
        if (spanData && typeof spanData === 'object') {
          try {
            delete spanData['http.request_content_length'];
            delete spanData['http.response_content_length'];
            delete spanData['http.decoded_response_content_length'];
            delete spanData['http.response_transfer_size'];
          } catch {
            // Frozen or sealed — skip
          }
        }
      }
    }

    scrubDemographicSurfaces(event);

    return event;
  } catch (e) {
    console.warn(
      '[Sentry] _beforeSendTransaction threw, passing event through:',
      (e as Error).message
    );
    return event;
  }
}

function beforeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb
): Sentry.Breadcrumb | null {
  // Guard: an exception here silently drops ALL subsequent breadcrumbs.
  try {
    if (breadcrumb?.category === 'console' && breadcrumb?.level === 'debug') {
      return null;
    }
    scrubDemographicRecord(breadcrumb?.data);
    return breadcrumb;
  } catch {
    return breadcrumb;
  }
}

// Safely create a Sentry integration with error handling. Returns null if
// creation fails (e.g. unsupported browser APIs on older WebViews).
function safeIntegration<T>(createFn: () => T, name: string): T | null {
  try {
    return createFn();
  } catch (e) {
    console.warn(
      `[Sentry] Failed to create ${name} integration:`,
      (e as Error).message
    );
    return null;
  }
}

type SentryIntegration = NonNullable<
  ReturnType<typeof Sentry.browserTracingIntegration>
>;

function buildIntegrations(router: Router | undefined): SentryIntegration[] {
  return [
    // Browser Tracing — automatic performance monitoring
    safeIntegration(
      () =>
        Sentry.browserTracingIntegration({
          router,
          enableInp: true,
          enableLongTask: true,
          enableLongAnimationFrame: true,
          enableHTTPTimings: true,
        }),
      'browserTracing'
    ),

    // Session Replay — buffer mode, error-gated. context.startSentrySession()
    // calls this integration's `startBuffering()` on every new kiosk session
    // (and `stop()` first, so one visitor's DOM never lands in the next
    // visitor's replay). Buffering records rrweb into a rolling in-memory
    // window and uploads NOTHING; the buffer is only converted into a real
    // replay when an error event is sampled against replaysOnErrorSampleRate
    // below. No error in a session => that session's buffer is dropped on the
    // next stop().
    //
    // Do NOT swap `startBuffering()` for the integration's `start()`: `start()`
    // means "record a full session replay regardless of sampling rate", which
    // is what silently billed us for ~3,300 replays/day while both sample
    // rates here read 0.
    //
    // The tuning below is load-bearing for buffer mode on Celeron kiosks:
    //   - Network bodies disabled (biggest CPU saver — prevents serialization
    //     storms on WiFi-drop retries)
    //   - Compression disabled (Worker fallback is what burns CPU on Celerons)
    //   - Mutation limit lowered (auto-stops recording on DOM storms)
    //   - Max duration capped at 5 min (prevents unbounded buffer growth offline)
    // Buffer-mode memory is additionally bounded by the SDK itself: it forces
    // an rrweb checkout every 60s, so the retained window stays ~1 min.
    safeIntegration(
      () =>
        Sentry.replayIntegration({
          maskAllText: false, // no text mask: personal data has .sentry-block
          maskAllInputs: false, // owned kiosks, full visibility needed
          blockAllMedia: false, // allow UI media (icons, backgrounds)

          // GDPR: block face/portrait images (biometric data under Art. 9).
          // Using CSS-class block rather than blockAllMedia which would also
          // hide icons and style previews.
          block: ['.sentry-block'],

          networkDetailAllowUrls: [/\.theartisan\.ai/, /^\/api\//],
          networkCaptureBodies: false,
          networkRequestHeaders: ['X-Session-ID', 'X-Kiosk-Id'],
          networkResponseHeaders: ['X-Request-ID', 'X-Trace-ID'],

          minReplayDuration: 5000,
          maxReplayDuration: 300000, // 5 min — kiosk sessions are short

          mutationLimit: 5000,
          mutationBreadcrumbLimit: 500,

          // Batch replay flushes more aggressively so we hit Sentry's
          // ingest API less often. Defaults are 5000/5500ms = ~12 POSTs/min
          // PER ACTIVE REPLAY × the session sample rate × N kiosks → easily
          // trips Sentry's per-second rate limit (HTTP 429 in console).
          // 30s windows give ~6x fewer requests with the same total
          // payload; offline transport already queues failed sends, so
          // the trade-off is just a slightly bigger in-memory buffer
          // (mutationLimit=5000 caps that anyway). On a hard crash we
          // lose at most 30s of replay instead of 5s — acceptable for
          // a 24/7 kiosk where the value of replay is debugging multi-
          // step UX flows, not last-second pre-crash state.
          flushMinDelay: 30_000,
          flushMaxDelay: 30_500,

          useCompression: false,
        }),
      'replay'
    ),

    // app.js owns uncaught-error + unhandled-rejection capture. Sentry's
    // built-in GlobalHandlers is disabled here because its legacy
    // window.onerror / window.onunhandledrejection hooks drop the thrown Error
    // object on WebKit (Chrome iOS / Safari) — delivering no error and a bogus
    // "undefined" filename for stack-overflow RangeErrors — which produced
    // stackless, sourcemap-proof events (KIOSK-FRONTEND-3N/10Q). app.js
    // recaptures the real ErrorEvent.error / PromiseRejectionEvent.reason via
    // captureException, which reads the object's own stack.
    safeIntegration(
      () =>
        Sentry.globalHandlersIntegration({
          onerror: false,
          onunhandledrejection: false,
        }),
      'globalHandlers'
    ),

    safeIntegration(
      () => Sentry.browserProfilingIntegration(),
      'browserProfiling'
    ),
    safeIntegration(
      () => Sentry.extraErrorDataIntegration({ depth: 5 }),
      'extraErrorData'
    ),
    safeIntegration(() => Sentry.httpClientIntegration(), 'httpClient'),
    safeIntegration(
      () => Sentry.reportingObserverIntegration(),
      'reportingObserver'
    ),
  ].filter((v): v is SentryIntegration => v !== null);
}

export interface SentryInitOptions {
  app?: App;
  router?: Router;
}

// MUST be called before Vue app creation. Guards against double-init.
// If Sentry.init() itself throws, rolls back state so init can be retried.
// Post-init listener setup (beforeunload/visibilitychange/network change)
// is non-critical — failures there are logged but don't roll back.
export function initialize(options: SentryInitOptions = {}): void {
  if (state.initialized || state.initializing) {
    console.warn('[Sentry] Already initialized or initializing, skipping');
    return;
  }
  state.initializing = true;

  const { app, router } = options;

  const environment = getEnvironment();
  state.environment = environment;
  const isDevelopment = environment === 'development';

  // Dev-only opt-out: skip the whole SDK, not just event sending. Visual and
  // e2e harnesses set the flag before app boot; without this, the (inert)
  // Replay integration still installs rrweb observers whose debounced flush
  // mixes rAF and clearTimeout — a no-op in real browsers, but a thrown error
  // under Playwright's fake clock that poisons unrelated promise chains.
  if (
    isDevelopment &&
    (window as Window & { SENTRY_DISABLED?: boolean }).SENTRY_DISABLED
  ) {
    state.initializing = false;
    return;
  }

  try {
    Sentry.init({
      app,
      // CI ships nothing. The DSN is baked into the bundle, so a Playwright run
      // reports into the real production project just by loading the page —
      // telemetry about a browser nobody is using, billed at production rates.
      enabled: environment !== 'ci',
      // Development hosts get a DSN-less init (pipeline alive, nothing sent)
      // unless VITE_SENTRY_FORCE=true — see resolveSentryDsn.
      dsn: resolveSentryDsn(
        environment,
        (import.meta as ImportMeta & { env?: Record<string, string> }).env
          ?.VITE_SENTRY_FORCE === 'true'
      ),
      environment,
      // Release name is injected at build time by vite.config.js (which in
      // turn reads SENTRY_RELEASE from CI / falls back to git SHA locally).
      // The fallback must NOT look like a real semver: package.json is pinned
      // at 1.0.0 and builds that missed injection spent months masquerading
      // as release "1.0.0" (79% of Sentry volume, unsymbolicated). A
      // self-describing name makes any future injection gap filterable.
      release:
        ((import.meta as ImportMeta & { env?: Record<string, string> }).env
          ?.VITE_SENTRY_RELEASE as string | undefined) ||
        'kiosk-frontend@unreleased',
      sendDefaultPii: sendsDefaultPii(
        typeof window === 'undefined' ? '' : window.location.pathname
      ),
      // 50 instead of default 100 — high-frequency touch events and XHR
      // calls on a 24/7 kiosk fill the default buffer instantly.
      maxBreadcrumbs: 50,
      // Offline transport queues events when offline, sends when restored.
      transport: makeBrowserOfflineTransport(makeFetchTransport),
      // maxQueueSize isn't in the public BrowserTransportOptions type but is
      // honored by the offline transport at runtime.
      transportOptions: {
        maxQueueSize: 100,
      } as Sentry.BrowserOptions['transportOptions'],
      spotlight: isDevelopment,

      integrations: buildIntegrations(router),

      // tracesSampler replaces tracesSampleRate: KIOSK_TRACES_SAMPLE_RATE
      // still applies to everything except the analytics plumbing, which is
      // dropped at the head so the trace is never opened at all.
      tracesSampler: traceSampleRate as Sentry.BrowserOptions['tracesSampler'],
      tracePropagationTargets: TRACE_PROPAGATION_TARGETS,

      // Ambient (every-session) replay stays OFF. This fleet is ~95% of org
      // replay volume, and replays bill ~$0.018 each past the 25k/mo included
      // quota, so recording every visitor was the single largest line on the
      // Sentry bill.
      //
      // CAVEAT that cost us months of billing: this rate only gates the SDK's
      // OWN auto-start. The Replay integration's `start()` method explicitly
      // ignores it ("start a replay regardless of sampling rate"), so a manual
      // `start()` anywhere re-enables 100% recording with this line still
      // reading 0 — which is exactly what context.startSentrySession() did.
      // Anything that reaches for the integration by hand must use
      // `startBuffering()`, never `start()`.
      replaysSessionSampleRate: 0,

      // Keep every replay that actually has an error attached. This is the
      // rate the buffered session is sampled against when an error event goes
      // out; at 0 the buffer would record forever and never be sent, which is
      // strictly worse than not recording at all. Errored sessions are a tiny
      // fraction of the ~3,300 daily kiosk sessions, so 1.0 here is a small
      // absolute volume and every one of them is a replay someone will open.
      replaysOnErrorSampleRate: 1.0,

      // "UI Profile Hours" is the one line where 5-10% is not a cheap setting:
      // it has *zero* included quota and bills $0.25/hr, so the previous 0.1
      // was already ~175h/mo of pure overage. This app is 100% of that volume.
      // 0.01 keeps browser profiling alive on a 24/7 fleet — still thousands of
      // profiled sessions a month — for a bounded ~$4/mo.
      // Requires Document-Policy: js-profiling from the server (deploy/Caddyfile).
      profileSessionSampleRate: 0.01,
      profileLifecycle: 'trace',

      _experiments: { enableLogs: true },

      // Browser/platform noise that fires from the user's user-agent, not
      // from anything our code can fix. Each pattern is anchored to the
      // specific browser-level concept it's filtering so this list doesn't
      // accidentally swallow real bugs.
      ignoreErrors: BROWSER_PLATFORM_IGNORE_ERRORS,

      beforeSend,
      beforeSendTransaction:
        beforeSendTransaction as Sentry.BrowserOptions['beforeSendTransaction'],
      beforeBreadcrumb,
    });
  } catch (e) {
    // Roll back state so init() can be retried.
    state.initialized = false;
    state.initializing = false;
    console.error(
      '[Sentry] Sentry.init() failed, state rolled back:',
      (e as Error).message
    );
    throw e;
  }

  // Phase 2: SDK is now running. Mark initialized BEFORE auxiliary setup —
  // if listener setup below throws, the SDK is still fully functional; we
  // just lose some niceties. NOT rolling back here prevents a dangerous
  // state where the SDK is running but our service thinks it's not (would
  // cause all captureException/addBreadcrumb calls to silently no-op, plus
  // a retry of init() would double-initialize the Sentry SDK).
  state.initialized = true;

  try {
    state.beforeUnloadHandler = () => {
      Sentry.flush(2000);
    };
    state.visibilityChangeHandler = () => {
      if (document.visibilityState === 'hidden') {
        Sentry.flush(1000);
      }
    };

    window.addEventListener('beforeunload', state.beforeUnloadHandler);
    document.addEventListener(
      'visibilitychange',
      state.visibilityChangeHandler
    );

    setNetworkContext();
  } catch (e) {
    console.warn(
      '[Sentry] Post-init setup partially failed:',
      (e as Error).message
    );
  } finally {
    state.initializing = false;
  }
}

export async function flush(timeout = 2000): Promise<boolean | undefined> {
  if (!state.initialized) return;
  return Sentry.flush(timeout);
}
